/**
 * @deprecated This API client helper is deprecated. Use useAPIClientHelper hook instead.
 *
 * Migration guide:
 * 1. Replace ApiClientInstance() calls with useAPIClientHelper hook
 * 2. Use the apiClientInstance function returned from the hook
 * 3. Move API client logic into React components or custom hooks
 * 4. Use Redux hooks (useSelector, useDispatch) for state management
 *
 * @see useAPIClientHelper - The replacement hook for API client functionality
 * @see useSupportService - Example of successful migration using the new pattern
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { ApiClientInstance } from "utils/helpers";
 *
 * const SupportService = {
 *   getAllSupportCategories: () => {
 *     return ApiClientInstance(AuthEnums.AuthType.Company)
 *       .SupportCategoryClient.GetAsyncByFilter({...});
 *   }
 * };
 *
 * @example
 * // New way (recommended):
 * import useAPIClientHelper from "serviceHooks/useAPIClientHelper";
 *
 * function useSupportService() {
 *   const { apiClientInstance } = useAPIClientHelper();
 *
 *   const getAllSupportCategories = () => {
 *     return apiClientInstance(AuthEnums.AuthType.Company)
 *       .SupportCategoryClient.GetAsyncByFilter({...});
 *   };
 *
 *   return { getAllSupportCategories };
 * }
 *
 * Benefits of migration:
 * - Better React integration with hooks
 * - Improved state management with Redux hooks
 * - Better TypeScript support
 * - Cleaner separation of concerns
 * - Easier testing and mocking
 */

import axios, { AxiosInstance, ResponseType } from "axios";
import * as Sentry from "@sentry/react";
import { store } from "store";
import { displayErrorMessage } from "store/actions/utilActions";
import WintApiClient from "wint-api-client";
import { AuthEnums } from "models/enums";
import { LocalizationHelperInstance, SessionHelper, AuthHelper, EnvHelper } from "utils/helpers";
import { camelizeKeys } from "humps";
import { TranslationKeys } from "translations";
import { setAuthTokens } from "store/actions/authActions";
import { Person } from "models/stateTypes/authState";
import { ResponseError } from "models/utilTypes/apiClientTypes";
import { InvoiceConstants, ServiceConstants } from "utils/constants";
import { getRefreshTokenPromise, setRefreshTokenPromise } from "utils/helpers/refreshTokenLock";
import { decodeErrorBody } from "utils/helpers/errorBody";

let apiUrl = import.meta.env.VITE_SUPERKOLL_SERVER_URL ?? "/";
if (localStorage.getItem("API_ENVIRONMENT")) {
  apiUrl = localStorage.getItem("API_ENVIRONMENT") as string;
}

// Check if the current domain is appstaging.wint.se to route to apistaging.wint.se
if (window.location.hostname === "appstaging.wint.se") {
  apiUrl = "https://apistaging.wint.se";
}

// When running locally with SWA (localhost:4280), keep using "/"
// The proxy Azure Function will handle backend API calls to avoid CORS issues
// /api/content is handled by the content function, other /api/* go through proxy function

if (!EnvHelper.getIsProductionEnvironment()) {
  console.log(`API URL: ${apiUrl}`);
}

class ApiClient {
  http: AxiosInstance;

  constructor(
    baseUrl: string,
    authType: AuthEnums.AuthType,
    responseType: ResponseType = "json",
    contentType: string = "application/json",
    showErrors: boolean = false,
    skipUndefinedConversion: boolean = false,
    timeout?: number,
    hideErrorsForCodes?: string[]
  ) {
    this.http = axios.create();

    this.CreateClient(
      baseUrl,
      authType,
      responseType,
      contentType,
      showErrors,
      skipUndefinedConversion,
      timeout,
      hideErrorsForCodes
    );
  }

  GetClientInstance = () => {
    return new WintApiClient(this.http);
  };

  CreateClient = (
    baseUrl: string,
    authType: AuthEnums.AuthType,
    responseType: ResponseType = "json",
    contentType: string = "application/json",
    showErrors: boolean = false,
    skipUndefinedConversion: boolean = false,
    timeout?: number,
    hideErrorsForCodes?: string[]
  ) => {
    this.http.interceptors.request.use(
      (config) => {
        const headers = this.getHeaders(authType, contentType);
        config.baseURL = baseUrl;
        Object.entries(headers).forEach(([key, value]) => {
          if (value !== undefined) config.headers.set(key, value as string);
        });
        if (timeout !== undefined) config.timeout = timeout;
        config.responseType = responseType;
        return config;
      },
      (error) => this.onError(error, showErrors, hideErrorsForCodes)
    );

    this.http.interceptors.response.use(
      (response) => {
        if (response.data && responseType === "json") {
          response.data = camelizeKeys(
            skipUndefinedConversion
              ? response.data
              : JSON.parse(JSON.stringify(response.data, (key, value) => (value === null ? undefined : value))),
            function (key, convert) {
              return /[_]/.test(key) ? key.charAt(0).toLowerCase() + key.slice(1) : convert(key);
            }
          );
        }
        return response;
      },
      (error) => this.onError(error, showErrors, hideErrorsForCodes)
    );
  };

  private getHeaders = (authType: AuthEnums.AuthType, contentType: string) => {
    const currentLang = store.getState().common.locale;
    const state = store.getState().auth;
    const defaultHeader = {
      "Content-Type": contentType,
      "Accept-Language": currentLang.language === "sv" ? "sv-SE" : "en-US",
      "Wint-Client": import.meta.env.VITE_APP_NAME,
      "Wint-Client-Version": import.meta.env.VITE_VERSION,
    };

    switch (authType) {
      case AuthEnums.AuthType.User:
        return { ...defaultHeader, Authorization: `Bearer ${this.getAccessToken()}` };
      case AuthEnums.AuthType.Company:
        return {
          ...defaultHeader,
          Authorization: `Bearer ${this.getAccessToken()}`,
          CompanyId: state.selectedCompany.id.toString(),
          [ServiceConstants.CLIENT_CAPABILITIES_HEADER]: ServiceConstants.CLIENT_CAPABILITIES,
        };
      case AuthEnums.AuthType.Onboarding: {
        // getHeaders runs inside the request interceptor, so this throw is caught
        // by axios and normalized by onError into an empty ResponseError — the
        // message reaches neither the caller nor the console. Capture it first,
        // or a misroute here is silent. The throw itself still does the job that
        // matters: it stops the request before a 401 can trigger
        // AuthHelper.logout() and wipe the logged-in user's session.
        const misroute = new Error(
          "AuthType.Onboarding is not supported in the deprecated ApiClientInstance — use serviceHooks/useAPIClientHelper."
        );
        Sentry.captureException(misroute);
        throw misroute;
      }
      case AuthEnums.AuthType.Unauthorized:
      default:
        return defaultHeader;
    }
  };

  private getAccessToken = () => {
    const state = store.getState().auth;
    if (state.loggedInUser.authTokens && state.loggedInUser.authTokens.accessToken) {
      return state.loggedInUser.authTokens.accessToken;
    } else {
      //Session logout when migrate from basic auth to bearer auth
      AuthHelper.logout();
    }
  };

  private onError = (error: any, showErrors?: boolean, hideErrorsForCodes?: string[]) => {
    const data = decodeErrorBody(error?.response?.data);
    if (error?.response && error.response.status === 401 && !error.config?.url?.endsWith("/refresh")) {
      return this.refreshExpiredAccessToken(error);
    } else {
      showErrors &&
        store.dispatch(
          displayErrorMessage(
            // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
            error?.response?.status == 403
              ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.NotEnoughPermissions)
              : data
                ? this.getErrorDescriptions(data, hideErrorsForCodes)
                : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrong),
            { showSupportLink: this.needsSupportCase(data, hideErrorsForCodes) }
          )
        );
    }

    return Promise.reject({
      status: error?.response?.status,
      codes: this.getErrorCodes(data),
      message: this.getErrorDescriptions(data),
    });
  };

  /**
   * Only Wint can activate a booking account, so these errors are shown with the support-case link
   * rather than leaving the customer to find their own way to us. Reads the same filtered set
   * getErrorDescriptions shows, so a hidden code cannot attach the link to another error.
   */
  private needsSupportCase = (errors: any, hideErrorsForCodes?: string[]) =>
    this.getErrorCodes(errors)
      .filter((code) => !hideErrorsForCodes?.includes(code))
      .some((code) => InvoiceConstants.InactiveAccountErrorCodes.includes(code));

  private getErrorCodes = (errors: any) => {
    if (errors) {
      const errorObj = camelizeKeys(errors) as ResponseError[];
      if (Array.isArray(errorObj)) return errorObj.map((e) => e.code);
      const code = (errorObj as { code?: unknown } | null)?.code;
      return typeof code === "string" ? [code] : [];
    }
    return [];
  };

  // The support-case link is appended after the whole joined text, so these go last for the link
  // to follow the sentence it belongs to. The sort is stable: other errors keep their order.
  private inactiveAccountErrorsLast = (errorList: ResponseError[]) =>
    [...errorList].sort(
      (a, b) =>
        Number(InvoiceConstants.InactiveAccountErrorCodes.includes(a.code)) -
        Number(InvoiceConstants.InactiveAccountErrorCodes.includes(b.code))
    );

  private getErrorDescriptions = (errors: any, hideErrorsForCodes?: string[]) => {
    if (errors) {
      const errorObj = camelizeKeys(errors) as ResponseError[];
      return Array.isArray(errorObj)
        ? this.inactiveAccountErrorsLast(
            hideErrorsForCodes
              ? errorObj.filter((e) => hideErrorsForCodes.findIndex((hiddenCode) => hiddenCode === e.code) === -1)
              : errorObj
          )
            .map((e) => e.description)
            .join(", ")
            .replaceAll(":,", ":")
            .replaceAll(".,", ".")
        : typeof errorObj === "object" && errorObj !== null && "message" in errorObj
          ? (errorObj as any).message
          : "";
    }
    return "";
  };

  private refreshExpiredAccessToken = (error: any) => {
    const originalRequest = error.config;
    const session = SessionHelper.getSessionItem<Person>("user_session");
    const refreshToken = session?.authTokens?.refreshToken;

    if (refreshToken && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!getRefreshTokenPromise()) {
        const promise = ApiClientInstance(AuthEnums.AuthType.Unauthorized)
          .AuthClient.RefreshToken(refreshToken)
          .then((res) => {
            const currentSession = SessionHelper.getSessionItem<Person>("user_session");
            if (!currentSession) {
              return;
            }
            if (res.data.authTokens) {
              SessionHelper.saveToSession("user_session", { ...currentSession, authTokens: res.data.authTokens });
              store.dispatch(setAuthTokens(res.data.authTokens));
            }
          })
          .catch(() => {
            AuthHelper.logout();
            throw error;
          })
          .finally(() => {
            setRefreshTokenPromise(null);
          });
        setRefreshTokenPromise(promise);
      }

      return getRefreshTokenPromise()!.then(() => {
        return this.http.request(originalRequest);
      });
    } else {
      AuthHelper.logout();
      return Promise.reject(error);
    }
  };
}

/**
 * @deprecated Use useAPIClientHelper hook instead. This function is deprecated.
 * @see useAPIClientHelper - The replacement hook
 * @see useSupportService - Example of successful migration
 */
const ApiClientInstance = (
  type: AuthEnums.AuthType,
  params?: {
    showErrors?: boolean;
    hideErrorsForCodes?: string[];
    responseType?: ResponseType;
    contentType?: string;
    skipUndefinedConversion?: boolean;
    timeout?: number;
  }
) =>
  new ApiClient(
    apiUrl,
    type,
    params?.responseType,
    params?.contentType,
    params?.showErrors,
    params?.skipUndefinedConversion,
    params?.timeout,
    params?.hideErrorsForCodes
  ).GetClientInstance();

export default ApiClientInstance;
